// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginService } from '../../login/services/login.service';
import { NavService } from '@services/nav.service';
import { User, UserLogonState, UserTypes } from '../../interfaces/user.interfaces';
import { UserService } from '../../services/user.service';
import { ErrorService } from '../../services/error.service';
import { debounceTime, takeWhile } from 'rxjs';
import { SuccessModalService } from '@services/success-modal.service';
import { DealerProductEnrollments, DealerProducts } from '@interfaces/dealer.interfaces';
import { DealershipService } from '@services/dealership.service';
import { AppEventEx } from '@interfaces/interfaces';
import { AuthenticationStatus } from '@interfaces/auth.interfaces';

@Component({
    selector: 'app-pddj-login',
    templateUrl: './pddj-login.component.html',
    styleUrls: ['./pddj-login.component.scss'],
})
export class PddjLoginComponent implements OnInit {
    public isComponentAlive = true;
    public logInForm: FormGroup;
    public viewPassword = false;
    public isLoading = false;
    public isLoginShowing = true;
    public isVerifyShowing = false;
    public phone: string = '';
    private dealerEnrollments: DealerProductEnrollments[] = [];

    constructor(
        private readonly loginService: LoginService,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly navService: NavService,
        private readonly errorService: ErrorService,
        private readonly successModalService: SuccessModalService,
        private readonly dealershipService: DealershipService
    ) {}

    ngOnInit(): void {
        this.navService.setTitle('Login');

        this.logInForm = new FormGroup({
            email: new FormControl(''),
            password: new FormControl(''),
        });

        this.logInForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.logInForm.controls.email.setValue(value.trim());
            });
    }

    async authenticateFailure(): Promise<void> {
        this.isLoading = false;
    }

    async authenticateSuccess(user: User): Promise<void> {
        if (user) {
            this.loginService
                .logLoginState(user.userId, user.logonId, UserLogonState.LogonProcessComplete)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe();

            this.dealershipService
                .getDealerEnrollment$(user && user.dealerId)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((enrollments) => {
                    this.isLoading = false;
                    this.dealerEnrollments = enrollments;
                    if (!this.dealerHasPddj(user)) {
                        this.errorService
                            .showErrorAtService({
                                statusCode: 500,
                                message:
                                    'Your current dealer must be enrolled in the EZ DOCS Automate product in order to have access to PDDJ. Switch the active dealer in the EZ DOCS app to a dealer that is enrolled in EZ DOCS Automate product to gain access.',
                                title: 'Access to EZ DOCS Automate required',
                            })
                            .afterClosed()
                            .subscribe(() => {
                                this.router.navigate(['/pddj', 'login']);
                                this.isVerifyShowing = false;
                                this.isLoginShowing = true;
                            });
                    } else {
                        this.router.navigate(['/pddj', 'status']);
                    }
                });
        }
    }

    authenticateTokenFailure(): void {
        this.authenticateFailure();
    }

    authenticateTokenSuccess(user: User): void {
        this.authenticateSuccess(user);
    }

    getUser(email: string, password: string): Promise<void> {
        return new Promise((success, fail) => {
            this.loginService.authenticateUser(
                email,
                password,
                null,
                (auth) => {
                    const user = auth.user;

                    if (!user) {
                        this.isLoading = false;
                        this.errorService.showError({
                            statusCode: 500,
                            message:
                                'Please check your email and / or password to ensure you have entered them correctly. If you have forgotten your password, close this window and click on Reset Password to create a new one. If you cannot remember your email address, contact the dealership.',
                            title: 'Login Attempt Unsuccessful',
                        });

                        fail();
                    } else {
                        this.authenticateSuccess(user);
                        success();
                    }
                },
                () => {
                    this.authenticateFailure();
                    fail();
                },
                (status) => this.mfaCallback(status.phoneValidation)
            );
        });
    }

    dealerHasPddj(user: User): boolean {
        const activation = this.dealerEnrollments.find((x) => x.productId === DealerProducts.EzDocsAutomate)?.activationDate;
        const turnOffAccess = this.dealerEnrollments.find((x) => x.productId === DealerProducts.EzDocsAutomate)?.turnOffAccessDate;
        const currentDate = new Date();
        let hasPrintToDDJ = false;

        if (user && user.userType === UserTypes.Internal) {
            hasPrintToDDJ = true;
        } else {
            if (activation && new Date(activation) < currentDate) {
                if (turnOffAccess && new Date(turnOffAccess) <= currentDate) {
                    hasPrintToDDJ = false;
                } else {
                    hasPrintToDDJ = true;
                }
            } else {
                hasPrintToDDJ = false;
            }
        }

        return hasPrintToDDJ;
    }

    logonChange() {
        this.isLoginShowing = true;
        this.isVerifyShowing = false;
        this.logInForm.reset();
    }

    // login method
    async logInSubmit(): Promise<void> {
        if (this.logInForm.status !== 'VALID') {
            return;
        }

        this.isLoading = true;
        const { email, password } = this.logInForm.value;

        await this.getUser(email, password);
    }

    async mfaCallback(phone: string): Promise<void> {
        this.isLoginShowing = false;
        this.isVerifyShowing = true;
        this.phone = phone;
        this.isLoading = false;
    }

    public passwordVisible(): void {
        this.viewPassword = !this.viewPassword;
    }

    verifyChange(event: AppEventEx<AuthenticationStatus>) {
        if (event.context == 'success') {
            this.authenticateTokenSuccess(event.data.user);
        } else if (event.context == 'newToken') {
            this.successModalService.showSuccess({
                title: 'Verification Code Sent',
                content:
                    'A new verification code has been sent. Please close this window and enter the new code. If you do not receive the code within 5 minutes, click “Request New Code” to start over. Note: the verification code is only valid for 5 minutes.',
            });
            const { email, password } = this.logInForm.value;
            const user = this.userService.user;
            this.loginService.authenticateUser(
                email,
                password,
                user.phone,
                (auth) => this.authenticateSuccess(auth.user),
                () => this.authenticateFailure(),
                (state) => this.mfaCallback(state.phoneValidation)
            );
        } else {
            this.authenticateTokenFailure();
        }
    }
}
